import { useNewRelicAgent } from "@wettercom/maps-common/src/errors/newrelic/components/useNewRelicAgent";
import type { SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";
import { envConfig } from "../../config/envConfig";
import { useCurrentLocale } from "../../i18n/useCurrentLocale";
import { useGlobalState } from "../../state/hooks/useGlobalState";

export const SWR_CACHE_KEY_FOR_ALL_BACKEND_API_REQUESTS = "backend-api-data";

type RequestCacheKeys = {
    cacheKey: string;
    requestLocale: string;
    backendApiMarkerKey: string;
    refetchAfterCacheClear: boolean;
} | null;

type BackendAPITransformRequestFunction<InputType, OutputType> = (
    inputData: InputType,
) => OutputType;

type UseBackendAPIRequestOptions = {
    skip?: boolean;
    suspense?: boolean;
    requestCacheKey?: string | undefined;
    refetchAfterCacheClear?: boolean;
};

export function useBackendAPIRequest<APIResponseType, TransformedType>(
    requestUrl: string | undefined,
    transformRequestFn: BackendAPITransformRequestFunction<
        APIResponseType,
        TransformedType
    >,
    options?: UseBackendAPIRequestOptions,
) {
    const { skip: skipRequest = false, suspense = false } = options ?? {};

    const { logErrorToNewRelic } = useNewRelicAgent();
    const { localeCodeForMatlocqRequests } = useCurrentLocale();
    const { setDataRefreshNeeded } = useGlobalState();

    const urlAndCacheKeys: RequestCacheKeys =
        !skipRequest && requestUrl !== undefined
            ? {
                  cacheKey: options?.requestCacheKey ?? requestUrl,
                  // current locale must be an additional cache key because backend api may return different data per requested locale
                  requestLocale: localeCodeForMatlocqRequests,
                  // - add static cache key to identify all backend api requests so that we can invalidate their cache later
                  backendApiMarkerKey:
                      SWR_CACHE_KEY_FOR_ALL_BACKEND_API_REQUESTS,
                  refetchAfterCacheClear:
                      options?.refetchAfterCacheClear ?? true,
              }
            : null;

    return useSWRImmutable<
        TransformedType,
        Error,
        RequestCacheKeys,
        SWRConfiguration<TransformedType>
    >(
        urlAndCacheKeys,
        async () => {
            if (requestUrl === undefined)
                throw Error(
                    "requestUrl is undefined. This should normally not happen.",
                );

            const fullBackendApiUrl = requestUrl.startsWith("https://")
                ? requestUrl
                : `${envConfig.api.baseUrl}${requestUrl}`;

            const response = await fetch(fullBackendApiUrl, {
                headers: {
                    token: envConfig.api.authToken,
                    "x-application-id": envConfig.api.authApplicationId,
                    "accept-language": localeCodeForMatlocqRequests,
                },
            });

            if (!response.ok) {
                const error = new Error(
                    "Could not fetch data from backend api.",
                );

                if (response.status === 410) {
                    setDataRefreshNeeded(true);
                } else {
                    logErrorToNewRelic(error, {
                        statusCode: response.status,
                        statusText: response.statusText,
                        requestedUrl: fullBackendApiUrl,
                    });
                }

                throw error;
            }

            const responseJson: TransformedType = await response.json();

            // TODO: validate responses with zod

            // @ts-expect-error TODO: how to avoid that?
            return transformRequestFn(responseJson);
        },
        {
            suspense,
        },
    );
}
